import App from '@/App';
import SimpleApp from '@/SimpleApp';
import '@/globals/aliases';
import '@/globals/urlsearchparams-guard';
import '@/i18n';
import i18n from '@/i18n';
import '@/index.css';
import { shouldIgnoreGlobalError } from '@/lib/error-filter';
import '@/prebootstrap'; // Ensure this runs first
import { getCanonicalAdminUrl } from '@packages/config/domainRedirect';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
// Sentry is loaded dynamically in sentry.init.ts to avoid dev mode errors
// Only load Sentry if DSN is configured and not in dev mode
if (import.meta.env.VITE_SENTRY_DSN && import.meta.env.VITE_ENV_LABEL !== 'DEV') {
  // Dynamic import to prevent Vite from analyzing Sentry dependencies in dev
  import('@/sentry.init').catch(() => {
    // Silent fail - Sentry is optional
  });
}

// Declare global window interface extension
declare global {
  interface Window {
    __SUPABANK_ERROR_LOGGERS__?: boolean;
  }
}

export function createAppRoot(opts: {
  enableMockApi?: boolean;
  shell?: 'default' | 'simple';
} = {}) {
  const { shell = 'default' } = opts;

  // 1. Admin Canonical Redirect Logic
  if (typeof window !== 'undefined') {
    try {
      const host = window.location.hostname || '';
      const path = window.location.pathname || '';
      const isAdminPath = path.startsWith('/admin');
      
      if (isAdminPath) {
        const canonicalOrigin = getCanonicalAdminUrl(host);
        
        if (canonicalOrigin) {
          const nextUrl = `${canonicalOrigin}${window.location.pathname}${window.location.search}${window.location.hash}`;
          if (nextUrl !== window.location.href) {
            window.location.replace(nextUrl);
            return; // Stop execution if redirecting
          }
        }
      }
    } catch (error) {
      console.warn('[admin-canonical-redirect] failed', error);
    }

    // 2. Global Error Loggers
    if (!window.__SUPABANK_ERROR_LOGGERS__) {
      window.__SUPABANK_ERROR_LOGGERS__ = true;

      window.addEventListener('error', (event) => {
        if (shouldIgnoreGlobalError(event)) return;
        console.error('[supabank:global-error]', event.error ?? event.message, {
          source: event.filename,
          line: event.lineno,
          column: event.colno,
        });
      });

      window.addEventListener('unhandledrejection', (event) => {
        console.error('[supabank:unhandled-rejection]', event.reason);
      });
    }
  }

  // 3. Render Logic
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  const root = createRoot(rootElement);

  if (shell === 'simple') {
    root.render(<SimpleApp />);
  } else {
    root.render(
      <I18nextProvider i18n={i18n}>
        <App />
      </I18nextProvider>
    );
  }
}
